import { Bot, MessageSquare, Globe, Mail, Phone, Users, Calendar, Webhook } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const services = [
  {
    title: 'Customer Support Chatbots',
    icon: Bot,
    description: 'AI responds instantly to FAQs, reducing workload by automating customer support 24/7.',
    benefits: ['Instant responses', '24/7 availability', 'Reduced workload']
  },
  {
    title: 'WhatsApp AI Integration',
    icon: MessageSquare,
    description: 'Automate sales & customer interactions on WhatsApp with intelligent AI responses.',
    benefits: ['Automated messaging', 'Sales automation', 'Quick response time']
  },
  {
    title: 'AI Website Development',
    icon: Globe,
    description: 'AI-enhanced, high-converting websites built for automation and optimal user experience.',
    benefits: ['Smart optimization', 'High conversion', 'Automated updates']
  },
  {
    title: 'Email Automation',
    icon: Mail,
    description: 'AI follows up with leads, nurturing them into customers through smart email sequences.',
    benefits: ['Lead nurturing', 'Smart follow-ups', 'Conversion tracking']
  },
  {
    title: 'AI Voice Callers',
    icon: Phone,
    description: 'AI-powered phone calls for customer service & lead qualification with natural conversations.',
    benefits: ['Natural dialogue', 'Lead scoring', '24/7 calling']
  },
  {
    title: 'Automated Lead Generation',
    icon: Users,
    description: 'AI finds & engages potential clients, automatically qualifying and nurturing leads.',
    benefits: ['Lead discovery', 'Auto-qualification', 'Smart engagement']
  },
  {
    title: 'AI Appointment Setters',
    icon: Calendar,
    description: 'AI schedules meetings with qualified leads, managing your calendar automatically.',
    benefits: ['Smart scheduling', 'Calendar sync', 'Follow-up reminders']
  },
  {
    title: 'Integration Hub',
    icon: Webhook,
    description: 'Connect all your tools and automate workflows with our powerful integration platform.',
    benefits: ['Seamless sync', 'Custom workflows', 'API access']
  }
];

export const Services = () => {
  return (
    <section id="services" className="py-20 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl font-bold text-cyan-400 neon-text mb-4">
            What We Automate
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Transform your business with our comprehensive suite of AI automation solutions
          </p>
        </motion.div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              viewport={{ once: true }}
              className="service-card group p-6 rounded-lg"
            >
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-lg bg-cyan-400/10 group-hover:bg-cyan-400/20 transition-colors">
                  <service.icon className="w-8 h-8 text-cyan-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-cyan-300 mb-2">
                    {service.title}
                  </h3>
                  <p className="text-gray-400 mb-4">{service.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {service.benefits.map((benefit, i) => (
                      <span
                        key={i}
                        className="text-xs px-2 py-1 rounded-full bg-cyan-400/10 text-cyan-300"
                      >
                        {benefit}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <Link
            to="/audit"
            className="cta-button inline-flex items-center gap-2 text-lg font-semibold"
          >
            Get Your AI Automation Audit →
          </Link>
        </motion.div>
      </div>
    </section>
  );
};